import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Send, Sparkles, AlertCircle, XCircle, Save, Check } from "lucide-react";
import { entities, extractTextFromFiles, streamInvokeLLM, getDisplayMessage } from "@/api/apiClient";
import { buildEditalContextForPerguntarIa } from "@/lib/buildEditalContextForIa";
import { normalizarArquivosRelacionadosLegado } from "@/lib/resolverDocumentosProcesso";
import { selecionarTrechosRelevantes } from "@/lib/selecionarTrechosRelevantes";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/** Limite de caracteres do texto extraído mantido em cache (alinhado ao limite do servidor). */
const MAX_CONTEXT_CHARS = 450_000;

/**
 * Orçamento de caracteres dos trechos dos documentos enviados por pergunta.
 * ~8–10k tokens de documentos: TTFT bem menor que 60k chars sem perder a maioria das respostas.
 */
const TRECHOS_BUDGET_CHARS = 28_000;

/** Quantidade de mensagens anteriores enviadas como histórico (evita prompt crescer sem limite). */
const MAX_HISTORICO_MENSAGENS = 4;

/** Limite de saída da IA no chat (respostas objetivas; menos tokens = fim mais rápido). */
const MAX_TOKENS_RESPOSTA = 2_000;

/** Intervalo mínimo entre atualizações do balão durante o stream (evita re-render a cada token). */
const STREAM_UI_THROTTLE_MS = 80;

/** Apenas formatos cujo texto o servidor consegue extrair (PDF + imagens via OCR). */
const EXT_LEGIVEIS = /\.(pdf|jpg|jpeg|png)$/i;

const SYSTEM_PROMPT = `Você é um assistente especializado em licitações públicas brasileiras.
Responda às perguntas do usuário SOMENTE com base no RESUMO ESTRUTURADO e no TEXTO INTEGRAL dos documentos fornecidos (Edital, Termo de Referência e Estudo Técnico Preliminar/ETP já processados).

Regras:
- Utilize apenas as informações presentes nesses documentos. Nunca invente valores, prazos, cláusulas ou exigências.
- Se a informação não constar nos documentos, responda exatamente: "Não localizei essa informação nos documentos processados."
- Você não faz pesquisa externa (internet, PNCP, ComprasNet, TCU etc.); se a pergunta depender disso, explique que a resposta depende de consulta a fontes externas.
- Cite, quando útil, em qual documento ou seção a informação aparece.
- Seja direto e conciso (priorize a resposta em poucas frases ou uma lista curta). Use Markdown só quando ajudar (tabelas/listas).`;

/** Converte o Markdown da resposta em texto plano legível (sem marcações/HTML). */
function markdownParaTextoPlano(md) {
  if (!md) return "";
  let t = String(md).replace(/\r\n/g, "\n");
  t = t.replace(/```([\s\S]*?)```/g, (_m, code) => code.replace(/^[a-z]*\n/i, "")); // blocos de código
  t = t.replace(/`([^`]+)`/g, "$1"); // código inline
  t = t.replace(/^\s{0,3}#{1,6}\s+/gm, ""); // títulos
  t = t.replace(/\*\*([^*]+)\*\*/g, "$1"); // negrito **
  t = t.replace(/__([^_]+)__/g, "$1"); // negrito __
  t = t.replace(/\*([^*]+)\*/g, "$1"); // itálico *
  t = t.replace(/^\s*>\s?/gm, ""); // citações
  t = t.replace(/^\s*[-*+]\s+/gm, "• "); // listas
  t = t.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1 ($2)"); // links
  t = t.replace(/^\s*\|?[\s:|-]+\|?\s*$/gm, ""); // linhas separadoras de tabela
  t = t.replace(/^\s*\|(.*)\|\s*$/gm, (_m, row) =>
    row
      .split("|")
      .map((c) => c.trim())
      .filter(Boolean)
      .join(" — ")
  ); // linhas de tabela -> "a — b — c"
  t = t.replace(/\n{3,}/g, "\n\n").trim();
  return t;
}

/** Segurança: se o modelo devolver {"resposta": "..."}, extrai apenas o texto. */
function desembrulharResposta(texto) {
  const t = String(texto || "").trim();
  if (t.startsWith("{") && /["']resposta["']\s*:/.test(t)) {
    try {
      const obj = JSON.parse(t);
      if (obj && typeof obj.resposta === "string") return obj.resposta.trim();
    } catch {
      const m = t.match(/["']resposta["']\s*:\s*"([\s\S]*)"\s*}?\s*$/);
      if (m) {
        try {
          return JSON.parse(`"${m[1]}"`).trim();
        } catch {
          return m[1].replace(/\\n/g, "\n").replace(/\\"/g, '"').trim();
        }
      }
    }
  }
  return t;
}

function escaparHtml(s) {
  return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** Texto plano com quebras de linha preservadas para o editor (apenas <br/>, sem marcação). */
function textoParaHtmlSimples(texto) {
  return escaparHtml(texto).replace(/\n/g, "<br/>");
}

/** URLs dos documentos principais (Edital, TR, ETP) que a IA consegue ler. */
function coletarDocumentosPrincipais(edital) {
  return normalizarArquivosRelacionadosLegado(
    edital?.arquivos_relacionados ?? edital?.arquivosRelacionados,
    edital?.arquivo_original ?? edital?.arquivoOriginal
  )
    .filter((d) => ["edital", "termo_referencia", "etp"].includes(d.tipo) && d.url)
    .filter((d) => EXT_LEGIVEIS.test(d.nome || d.url || ""))
    .map((d) => d.url);
}

export default function PerguntarIaDialog({ edital, open, onOpenChange, onUpdate }) {
  const [mensagens, setMensagens] = useState([]);
  const [pergunta, setPergunta] = useState("");
  const [loading, setLoading] = useState(false);
  const [carregandoDocs, setCarregandoDocs] = useState(false);
  const [erroDocs, setErroDocs] = useState("");
  const [docsTexto, setDocsTexto] = useState("");
  const [salvandoIdx, setSalvandoIdx] = useState(null);
  const [salvosIdx, setSalvosIdx] = useState(() => new Set());
  const abortRef = useRef(null);
  const scrollEndRef = useRef(null);
  const infoComplRef = useRef(edital?.informacoes_complementares || "");

  const fileUrls = useMemo(() => coletarDocumentosPrincipais(edital), [edital]);
  const contexto = useMemo(() => buildEditalContextForPerguntarIa(edital), [edital]);
  const streamUiTimerRef = useRef(null);
  const streamPendingRef = useRef("");

  useEffect(() => {
    infoComplRef.current = edital?.informacoes_complementares || "";
  }, [edital?.informacoes_complementares]);

  useEffect(() => {
    scrollEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [mensagens, loading, carregandoDocs]);

  const carregarDocumentos = useCallback(async () => {
    if (carregandoDocs || docsTexto || fileUrls.length === 0 || !edital?.id) return;
    setCarregandoDocs(true);
    setErroDocs("");
    try {
      const chunks =
        edital?.dados_extraidos?.extract_chunks ?? edital?.dadosExtraidos?.extract_chunks;
      if (Array.isArray(chunks) && chunks.length > 0) {
        let fromChunks = chunks
          .map((c, i) => `[Trecho ${i + 1} — ${c.tipo}] ${c.titulo_secao || ""}\n${c.texto || ""}`)
          .join("\n\n---\n\n");
        if (fromChunks.length > MAX_CONTEXT_CHARS) {
          fromChunks =
            fromChunks.slice(0, MAX_CONTEXT_CHARS) + "\n\n[... índice de trechos truncado ...]";
        }
        setDocsTexto(fromChunks);
        return;
      }

      // 1) Cache persistido válido (mesmas URLs dos documentos atuais)?
      const cache = await entities.Edital.getTextoDocumentosIa(edital.id).catch(() => null);
      const mesmasUrls =
        Array.isArray(cache?.urls) &&
        cache.urls.length === fileUrls.length &&
        cache.urls.every((u, i) => u === fileUrls[i]);
      if (cache?.texto && mesmasUrls) {
        setDocsTexto(cache.texto);
        return;
      }

      // 2) Extrai o texto integral e persiste no edital.
      const r = await extractTextFromFiles({ file_urls: fileUrls });
      let texto = r?.documentText || "";
      if (!texto) throw new Error("Não foi possível extrair o texto dos documentos.");
      if (texto.length > MAX_CONTEXT_CHARS) {
        texto = texto.slice(0, MAX_CONTEXT_CHARS) + "\n\n[... texto truncado por limite ...]";
      }
      setDocsTexto(texto);

      try {
        await entities.Edital.update(edital.id, {
          dados_extraidos: {
            texto_documentos_ia: {
              texto,
              urls: fileUrls,
              extraido_em: new Date().toISOString(),
            },
          },
        });
      } catch (persistErr) {
        // Persistência é otimização; falha aqui não impede o uso na sessão atual.
        console.error("Falha ao persistir texto dos documentos:", persistErr);
      }
    } catch (err) {
      setErroDocs(getDisplayMessage(err, "Não foi possível carregar os documentos para a IA."));
    } finally {
      setCarregandoDocs(false);
    }
  }, [carregandoDocs, docsTexto, fileUrls, edital]);

  useEffect(() => {
    if (open) carregarDocumentos();
  }, [open, carregarDocumentos]);

  const handleCancelar = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  /** Salva a resposta como TEXTO (Markdown convertido em texto plano) em Informações Complementares (aba Exigências). */
  const salvarEmInformacoes = async (idx) => {
    if (!edital?.id || salvandoIdx != null) return;
    const resposta = mensagens[idx]?.content?.trim();
    if (!resposta) return;
    const perguntaAssociada =
      idx > 0 && mensagens[idx - 1]?.role === "user" ? mensagens[idx - 1].content.trim() : "";

    setSalvandoIdx(idx);
    try {
      const respostaTexto = markdownParaTextoPlano(resposta);
      const cabecalhoTexto = `Pergunte a IA${perguntaAssociada ? `: ${perguntaAssociada}` : ""}`;
      const blocoTexto = `${cabecalhoTexto}\n\n${respostaTexto}`;
      const blocoHtml = textoParaHtmlSimples(blocoTexto);
      const atual = infoComplRef.current || "";
      const novo = atual ? `${atual}<br/><br/>${blocoHtml}` : blocoHtml;

      const atualizado = await entities.Edital.update(edital.id, { informacoes_complementares: novo });
      infoComplRef.current = novo;
      setSalvosIdx((prev) => new Set(prev).add(idx));
      toast.success("Resposta salva em Informações Complementares (aba Exigências).");
      onUpdate?.(atualizado);
    } catch (err) {
      toast.error(getDisplayMessage(err, "Erro ao salvar a resposta. Tente novamente."));
    } finally {
      setSalvandoIdx(null);
    }
  };

  const enviar = async () => {
    const texto = pergunta.trim();
    if (!texto || loading || carregandoDocs) return;
    if (!docsTexto) {
      toast.error("Os documentos ainda não foram carregados.");
      return;
    }

    const historico = [...mensagens, { role: "user", content: texto }];
    setMensagens(historico);
    setPergunta("");
    setLoading(true);
    abortRef.current = new AbortController();

    try {
      const historicoRecorte = historico.slice(-MAX_HISTORICO_MENSAGENS);
      const historicoTexto = historicoRecorte
        .map((m) => `${m.role === "user" ? "Pergunta" : "Resposta"}: ${m.content}`)
        .join("\n\n");

      // Consulta de recuperação: pergunta atual + últimas mensagens (para follow-ups).
      const consultaRecuperacao = historicoRecorte
        .filter((m) => m.role === "user")
        .map((m) => m.content)
        .join(" ");
      const trechos = selecionarTrechosRelevantes(docsTexto, consultaRecuperacao || texto, {
        budget: TRECHOS_BUDGET_CHARS,
      });
      const usouTrechos = trechos.length < docsTexto.length;

      const prompt = `--- RESUMO ESTRUTURADO DO EDITAL ---
${contexto}

--- ${usouTrechos ? "TRECHOS RELEVANTES DOS DOCUMENTOS" : "TEXTO INTEGRAL DOS DOCUMENTOS"} (Edital, Termo de Referência e ETP) ---
${trechos}

--- CONVERSA ATÉ AGORA ---
${historicoTexto}

Responda à última pergunta do usuário de forma direta e objetiva, usando o resumo estruturado e os documentos acima. Escreva a resposta em Markdown, sem envolver em JSON e sem blocos de código.`;

      // Adiciona um balão do assistente vazio que será preenchido conforme o stream chega.
      setMensagens((prev) => [...prev, { role: "assistant", content: "" }]);
      streamPendingRef.current = "";
      if (streamUiTimerRef.current) {
        clearTimeout(streamUiTimerRef.current);
        streamUiTimerRef.current = null;
      }

      const flushStreamUi = () => {
        streamUiTimerRef.current = null;
        const acumulado = streamPendingRef.current;
        setMensagens((prev) => {
          const next = [...prev];
          next[next.length - 1] = { role: "assistant", content: acumulado };
          return next;
        });
      };

      const full = await streamInvokeLLM({
        systemPrompt: SYSTEM_PROMPT,
        prompt,
        maxTokens: MAX_TOKENS_RESPOSTA,
        signal: abortRef.current.signal,
        onDelta: (_delta, acumulado) => {
          streamPendingRef.current = acumulado;
          if (streamUiTimerRef.current == null) {
            streamUiTimerRef.current = setTimeout(flushStreamUi, STREAM_UI_THROTTLE_MS);
          }
        },
      });

      if (streamUiTimerRef.current) {
        clearTimeout(streamUiTimerRef.current);
        streamUiTimerRef.current = null;
      }

      if (!full || !full.trim()) {
        throw new Error("A IA não retornou resposta. Tente novamente.");
      }
      const conteudoFinal = desembrulharResposta(full.trim());
      setMensagens((prev) => {
        const next = [...prev];
        next[next.length - 1] = { role: "assistant", content: conteudoFinal };
        return next;
      });
    } catch (err) {
      if (streamUiTimerRef.current) {
        clearTimeout(streamUiTimerRef.current);
        streamUiTimerRef.current = null;
      }
      // Remove o balão do assistente se ficou vazio (sem nenhum texto recebido).
      const removerPlaceholderVazio = (prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last?.role === "assistant" && !last.content) next.pop();
        return next;
      };
      if (err?.name === "AbortError") {
        setMensagens(removerPlaceholderVazio);
        toast.info("Consulta cancelada.");
        return;
      }
      toast.error(getDisplayMessage(err, "Erro ao consultar a IA. Tente novamente."));
      setMensagens(removerPlaceholderVazio);
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  };

  const podePerguntar = !!docsTexto && !carregandoDocs;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-500" />
            Pergunte a IA sobre o edital
          </DialogTitle>
          <DialogDescription>
            Tire dúvidas sobre o Edital, Termo de Referência e ETP processados. As respostas usam o
            conteúdo integral desses documentos.
          </DialogDescription>
        </DialogHeader>

        {fileUrls.length === 0 && !carregandoDocs && (
          <div className="flex items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            Nenhum documento (Edital, TR ou ETP) em PDF disponível para consulta neste edital.
          </div>
        )}

        {carregandoDocs && (
          <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-md p-3">
            <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
            Carregando documentos para a IA (apenas na primeira vez)...
          </div>
        )}

        {erroDocs && (
          <div className="flex items-center justify-between gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">
            <span className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {erroDocs}
            </span>
            <Button size="sm" variant="outline" onClick={carregarDocumentos}>
              Tentar novamente
            </Button>
          </div>
        )}

        <ScrollArea className="flex-1 pr-3 -mr-3">
          {mensagens.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 px-2 text-center">
              <p className="font-medium text-slate-600">Faça uma pergunta sobre o edital</p>
              <p className="mt-1">
                Ex.: &quot;Qual o prazo de entrega da proposta?&quot;, &quot;Quais documentos de
                habilitação são exigidos?&quot; ou &quot;Qual o valor estimado da contratação?&quot;
              </p>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              {mensagens.map((m, i) => (
                <div
                  key={i}
                  className={
                    m.role === "user" ? "flex justify-end" : "flex flex-col items-start gap-1"
                  }
                >
                  <div
                    className={`rounded-2xl px-4 py-2 text-sm max-w-[90%] ${
                      m.role === "user"
                        ? "bg-amber-600 text-white rounded-br-sm"
                        : "bg-slate-100 text-slate-800 rounded-bl-sm"
                    }`}
                  >
                    {m.role === "assistant" ? (
                      m.content ? (
                        loading && i === mensagens.length - 1 ? (
                          <span className="whitespace-pre-wrap">{m.content}</span>
                        ) : (
                          <div className="prose-sm [&_table]:my-2 [&_table]:w-full [&_th]:text-left [&_th]:px-2 [&_th]:py-1 [&_td]:px-2 [&_td]:py-1 [&_table]:border [&_th]:border [&_td]:border [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-2 last:[&_p]:mb-0">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                          </div>
                        )
                      ) : (
                        <span className="inline-flex items-center gap-2 text-slate-500">
                          <Loader2 className="h-4 w-4 animate-spin" /> Consultando os documentos...
                        </span>
                      )
                    ) : (
                      <span className="whitespace-pre-wrap">{m.content}</span>
                    )}
                  </div>
                  {m.role === "assistant" && m.content && !loading && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => salvarEmInformacoes(i)}
                      disabled={salvandoIdx != null || salvosIdx.has(i)}
                      className="h-7 px-2 text-xs text-amber-700 hover:bg-amber-50"
                    >
                      {salvandoIdx === i ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Salvando...
                        </>
                      ) : salvosIdx.has(i) ? (
                        <>
                          <Check className="h-3.5 w-3.5 mr-1" /> Salvo em Informações Complementares
                        </>
                      ) : (
                        <>
                          <Save className="h-3.5 w-3.5 mr-1" /> Salvar em Informações Complementares
                        </>
                      )}
                    </Button>
                  )}
                </div>
              ))}
              <div ref={scrollEndRef} />
            </div>
          )}
        </ScrollArea>

        <div className="flex items-end gap-2 border-t pt-3">
          <Textarea
            value={pergunta}
            onChange={(e) => setPergunta(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                enviar();
              }
            }}
            placeholder={
              podePerguntar
                ? "Digite sua pergunta sobre o edital... (Enter para enviar, Shift+Enter para quebrar linha)"
                : "Aguarde o carregamento dos documentos..."
            }
            rows={2}
            className="flex-1 resize-none"
            disabled={!podePerguntar || loading}
          />
          {loading ? (
            <Button
              type="button"
              variant="outline"
              onClick={handleCancelar}
              className="border-red-200 text-red-700 hover:bg-red-50"
            >
              <XCircle className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              onClick={enviar}
              disabled={!podePerguntar || !pergunta.trim()}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Send className="h-4 w-4" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
